import { useState } from 'react';
import type { Revision } from '../../shared/types';
import { useApi } from '../lib/hooks';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { Icon } from '../components/Icon';
import { Badge, Button, ErrorBox, Field, Loading, Modal, Panel, useAction } from '../components/ui';

/** The dashboard's code (private GitHub repository). */
const SOURCE_REPO = 'https://github.com/cracksmurf/RABD-Dash';

export function RevisionsPage() {
  const { data, error, loading, reload } = useApi<{ current: { scope: string; name: string; version: string }[]; revisions: Revision[] }>('/api/admin/revisions');
  const { can } = useMe();
  const [scope, setScope] = useState('any');
  const [adding, setAdding] = useState(false);
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return null;
  const name = (s: string) => data.current.find((c) => c.scope === s)?.name ?? s;
  const list = data.revisions.filter((r) => scope === 'any' || r.scope === scope);

  return (
    <div className="stack">
      <div className="repo-link">
        <Icon name="link" size={16} />
        <span>Source code: <a href={SOURCE_REPO} target="_blank" rel="noreferrer noopener">github.com/cracksmurf/RABD-Dash <Icon name="external" size={12} /></a></span>
        <span className="muted small">Private repository: only people with access on GitHub can open it.</span>
      </div>
      <div className="version-cards">
        {data.current.map((c) => (
          <button key={c.scope} className={`version-card ${scope === c.scope ? 'active' : ''}`} onClick={() => setScope(scope === c.scope ? 'any' : c.scope)}>
            <span>{c.name}</span><strong>v{c.version}</strong>
          </button>
        ))}
      </div>
      <div className="toolbar">
        <span className="muted grow">Versioning: <b>major</b> = breaking or large new capability, <b>minor</b> = new feature, <b>patch</b> = fixes. Platform and each tool have their own version. Entries from the code repository are added automatically on deploy.</span>
        {can('admin.revisions.manage') && <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>Add entry</Button>}
      </div>
      <div className="timeline">
        {list.map((r) => (
          <Panel key={r.id} className="rev">
            <div className="rev-head">
              <Badge tone={r.scope === 'platform' ? 'gold' : 'blue'}>{name(r.scope)}</Badge>
              <strong className="rev-version">v{r.version}</strong>
              <Badge tone={r.type === 'major' ? 'red' : r.type === 'minor' ? 'green' : 'neutral'}>{r.type}</Badge>
              <span className="grow rev-title">{r.title}</span>
              <span className="muted small">{r.date}{r.author ? ` · ${r.author}` : ''}{r.source === 'manual' ? ' · manual' : ''}</span>
            </div>
            <ul className="rev-changes">{r.changes.map((c, i) => <li key={i}>{c}</li>)}</ul>
          </Panel>
        ))}
      </div>
      {adding && <AddRevision scopes={data.current} onClose={() => setAdding(false)} onSaved={reload} />}
    </div>
  );
}

function AddRevision({ scopes, onClose, onSaved }: { scopes: { scope: string; name: string; version: string }[]; onClose: () => void; onSaved: () => void }) {
  const [scope, setScope] = useState('platform');
  const [version, setVersion] = useState('');
  const [type, setType] = useState('minor');
  const [title, setTitle] = useState('');
  const [changes, setChanges] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const { busy, run } = useAction();
  const cur = scopes.find((s) => s.scope === scope)?.version;
  return (
    <Modal title="Add revision entry" onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy === 's'} onClick={() => run('s', async () => {
        await api.post('/api/admin/revisions', { scope, version, type, title, date, changes: changes.split('\n').map((x) => x.trim()).filter(Boolean) });
        onSaved(); onClose();
      }, 'Revision added')}>Save</Button></>}>
      <div className="form-row">
        <Field label="Scope"><select value={scope} onChange={(e) => setScope(e.target.value)}>{scopes.map((s) => <option key={s.scope} value={s.scope}>{s.name}</option>)}</select></Field>
        <Field label="Version" hint={cur ? `Current: ${cur}` : undefined}><input value={version} placeholder="1.2.0" onChange={(e) => setVersion(e.target.value)} /></Field>
        <Field label="Type"><select value={type} onChange={(e) => setType(e.target.value)}><option value="major">major</option><option value="minor">minor</option><option value="patch">patch</option></select></Field>
        <Field label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      </div>
      <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
      <Field label="Changes" hint="One per line"><textarea rows={5} value={changes} onChange={(e) => setChanges(e.target.value)} /></Field>
    </Modal>
  );
}
